import ReactECharts from 'echarts-for-react';
import type { StyleConfig } from '../../../../types/bi';
import { useEChartsTheme } from '../../hooks/useEChartsTheme';
import { buildChartClickHandler } from '../../utils/drilldown';
import type { DrilldownEvent } from '../../utils/drilldown';

interface ScatterPlotProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  dimensions: string[];
  measures: { alias: string }[];
  style?: StyleConfig;
  onDrillDown?: (event: DrilldownEvent) => void;
}

export default function ScatterPlot({
  data,
  dimensions,
  measures,
  style,
  onDrillDown,
}: ScatterPlotProps) {
  const theme = useEChartsTheme();

  const xMeasure = measures[0]?.alias || data.columns[0];
  const yMeasure = measures[1]?.alias || data.columns[1];
  const sizeMeasure = measures[2]?.alias;
  const dimKey = dimensions[0];

  // Group data by dimension to create named series
  const grouped = new Map<string, number[][]>();
  for (const row of data.rows) {
    const group = dimKey ? String(row[dimKey] ?? 'All') : 'All';
    if (!grouped.has(group)) grouped.set(group, []);
    const x = Number(row[xMeasure]) || 0;
    const y = Number(row[yMeasure]) || 0;
    const point: number[] = [x, y];
    if (sizeMeasure) point.push(Number(row[sizeMeasure]) || 0);
    grouped.get(group)!.push(point);
  }

  // Compute max size for normalization
  const maxSize = sizeMeasure
    ? Math.max(1, ...data.rows.map((r) => Number(r[sizeMeasure]) || 0))
    : 1;

  const series = Array.from(grouped.entries()).map(([name, points], i) => ({
    name,
    type: 'scatter' as const,
    data: points,
    symbolSize: sizeMeasure ? (val: number[]) => Math.max(4, (val[2] / maxSize) * 40) : 10,
    itemStyle: style?.colours?.[i] ? { color: style.colours[i] } : undefined,
  }));

  const option = {
    tooltip: {
      trigger: 'item' as const,
      formatter: (params: { value: number[]; seriesName: string }) => {
        const v = params.value;
        let tip = `${params.seriesName}<br/>${xMeasure}: ${v[0]}<br/>${yMeasure}: ${v[1]}`;
        if (sizeMeasure && v[2] !== undefined) tip += `<br/>${sizeMeasure}: ${v[2]}`;
        return tip;
      },
    },
    legend: {
      show: grouped.size > 1,
      ...(style?.legend_position === 'none' ? { show: false } : {}),
    },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'value' as const, name: style?.axis_labels?.x || xMeasure },
    yAxis: { type: 'value' as const, name: style?.axis_labels?.y || yMeasure },
    series,
  };

  const events = buildChartClickHandler(dimensions, onDrillDown);

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      onEvents={events}
    />
  );
}
